"use client";

import anime from "animejs";
import clsx from "clsx";
import { useEffect, useRef } from "react";

export default function IncrementingNumber({
  number,
  label,
  valueShowsPlus,
  className,
  fadesIn,
  delay,
  separateNumberWithComma = true,
}: {
  number?: number | null;
  label?: string | null;
  valueShowsPlus?: boolean;
  className?: string;
  fadesIn?: boolean;
  delay?: number;
  separateNumberWithComma?: boolean | null;
}) {
  if (!number) return null;

  //We have a ref for the outer container so we can attach the
  //intersectionObserver to it, the fadeInContainerRef to animate in when the
  //fadeIn props is true, and the number/plusRef to animate on
  const outerContainerRef = useRef<HTMLDivElement>(null);
  const numberContainerRef = useRef<HTMLSpanElement>(null);
  const fadeInContainerRef = useRef<HTMLDivElement>(null);

  //Some of these figures might have a + so we want to animate that when the
  //number animation has ended.
  const plusRef = useRef<HTMLSpanElement>(null);

  //Save the observer so we can get rid of it once the animation has started
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    //If we don't have the number ref it doesn't make sense to animate
    if (!outerContainerRef || !outerContainerRef.current) return;
    observer.current = new IntersectionObserver(handleIntersection, {
      threshold: 1.0,
    });
    observer.current.observe(outerContainerRef.current);
  }, []);

  function handleIntersection(entries: IntersectionObserverEntry[]) {
    entries.forEach((entry) => {
      //This callback will be called when the number is 1px in viewport, so we
      //will call the animation function and then remove the
      //intersectinoObserver as we are never going to animate this again
      if (
        entry.isIntersecting &&
        numberContainerRef &&
        numberContainerRef.current
      ) {
        //Remove intersection observer
        observer.current!.unobserve(outerContainerRef.current!);

        //Play animation If there is a delay given in the props, we want this
        // timeline to start at a later point, which we do by setting a stagger
        // of 0 but starting that stagger amount by the props value.
        const timeline = anime.timeline({
          duration: 600,
          easing: "easeOutCubic",
          delay: anime.stagger(0, { start: delay }),
        });

        if (fadesIn && fadeInContainerRef && fadeInContainerRef.current) {
          timeline.add({
            targets: fadeInContainerRef.current,
            translateY: ["100%", "0%"],
            opacity: [0, 1],
            rotate: [-6, 0],
            complete: function () {
              if (!outerContainerRef.current) return;
              outerContainerRef.current.classList.remove("overflow-hidden");
            },
          });
        }

        //If we are fading in the element, we want to start counting before the
        //full fadein animation is completed, so we give it a relative delay to
        //make it start sooner.
        timeline.add(
          {
            targets: numberContainerRef.current,
            innerHTML: [0, number],
            round: 1,
            update: separateNumberWithComma
              ? function (anim) {
                  const val = anim.animations[0]!.currentValue;
                  numberContainerRef.current!.innerHTML = val.toLocaleString();
                }
              : undefined,
          },
          fadesIn ? "-=600" : "0",
        );

        if (valueShowsPlus && plusRef && plusRef.current) {
          timeline.add({
            targets: plusRef.current,
            opacity: [0, 1],
            duration: 600,
          });
        }
      }
    });
  }

  return (
    <div className="overflow-hidden" ref={outerContainerRef}>
      <div
        className={clsx(fadesIn && "translate-y-full rotate-6 opacity-0")}
        ref={fadeInContainerRef}
      >
        <div
          className={clsx(
            "text-heading-3 leading-none text-black ",
            className && className,
          )}
        >
          <span ref={numberContainerRef}>0</span>
          {valueShowsPlus && (
            <span className="opacity-0" ref={plusRef}>
              +
            </span>
          )}
        </div>
        {/* We still put the final number here so the label won't move along with the number as its animating + a 
        little extra padding as some number may take up more space than the final number*/}
        <div
          className={clsx(
            "h-0 pl-0.5 text-heading-3 leading-none text-black opacity-0",
          )}
        >
          {number}
          {valueShowsPlus && "+"}
        </div>
        {label && (
          <div className="text-body text-black/80 opacity-80">{label}</div>
        )}
      </div>
    </div>
  );
}
